/**
 * Where the signed-in user is kept in the browser, and the cheapest possible
 * question about it.
 *
 * Deliberately a leaf module with no imports. `services/auth.ts` owns real auth
 * behaviour, but it pulls in `api_helper`, which mutates `axios.defaults` at
 * module scope and so cannot be tree-shaken — importing anything from it drags
 * axios and Sentry along. The app entry (`index.tsx`) needs the storage key and
 * a presence check *before* it dynamically imports App, so those two live here
 * and the entry chunk stays lean.
 */

/** localStorage key holding the serialized signed-in user. */
export const AUTH_USER = "authUser";

/**
 * Whether a signed-in user is present in local storage.
 *
 * Intentionally NOT `services/auth.ts`'s `getUser()`: that helper calls
 * `isSessionExpired()` — which itself depends on the login mode being settled —
 * and can log the user out as a side effect. This is a pure read, safe to call
 * during bootstrap before anything is initialised.
 *
 * Returns false when storage is unavailable (privacy mode, blocked site data),
 * which callers should read as "assume signed out".
 */
export const hasStoredUser = (): boolean => {
  try {
    return localStorage.getItem(AUTH_USER) !== null;
  } catch {
    return false;
  }
};
